import React, { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormikProvider, useFormik } from "formik";
import { Page } from "shared/ui";
import { useModal } from "shared/hooks";
import { noop } from "shared/helpers";
import { AccessPoints } from "shared/routers";
import { adaptProcessTemplates } from "shared/adapters";
import { useParams } from "react-router-dom";
import {
  useGetAppProcessTemplatesWithNoParamsQuery,
  useGetMachineByIdQuery,
  UserItem,
} from "shared/api";
import { expandMachinesString, machinesInitialValues } from "shared/constants";
import { AddOrEditMachineSchema } from "shared/schemas";
import { MachinesTable, AddMachineModal, EditMachineModal } from "./ui";

interface MachinesProps {
  user?: UserItem;
}

const Machines: FC<MachinesProps> = ({ user }) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const hasAddPermission = user?.access_points?.includes(
    AccessPoints.MACHINES_ADD
  );

  const hasEditPermission = user?.access_points?.includes(
    AccessPoints.MACHINES_EDIT
  );

  const hasDeletePermission = user?.access_points?.includes(
    AccessPoints.MACHINES_DELETE
  );

  const { data: machine } = useGetMachineByIdQuery(
    {
      expand: expandMachinesString,
      id: Number(id),
    },
    { skip: !id }
  );

  const [isOpenAddMachineModal, showAddMachineModal, hideAddMachineModal] =
    useModal();

  const [isOpenEditMachineModal, showEditMachineModal, hideEditMachineModal] =
    useModal();

  useEffect(() => {
    if (machine) {
      showEditMachineModal();
    }
  }, [machine]);

  const { data: processTemplates } =
    useGetAppProcessTemplatesWithNoParamsQuery();

  const adaptedProcessTemplates = useMemo(() => {
    return adaptProcessTemplates(processTemplates || []);
  }, [processTemplates]);

  const addOrUpdateMachineFormik = useFormik({
    initialValues: machine
      ? { ...machine, process_template: machine?.process_template?.id }
      : machinesInitialValues,
    validationSchema: AddOrEditMachineSchema,
    enableReinitialize: true,
    onSubmit: noop,
  });

  return (
    <FormikProvider value={addOrUpdateMachineFormik}>
      <Page
        title={t("machines.machines-title")}
        buttonTitle={
          hasAddPermission ? t("machines.add-machines-btn")! : undefined
        }
        onClick={hasAddPermission ? showAddMachineModal : undefined}
      >
        <MachinesTable
          hasEditPermission={hasEditPermission}
          hasDeletePermission={hasDeletePermission}
          showEditMachineModal={showEditMachineModal}
          processTemplates={processTemplates || []}
        />

        <AddMachineModal
          isOpenModal={isOpenAddMachineModal}
          hideAddMachineModal={hideAddMachineModal}
          adaptedProcessTemplates={adaptedProcessTemplates}
        />

        <EditMachineModal
          isOpenModal={isOpenEditMachineModal}
          hideEditMachineModal={hideEditMachineModal}
          adaptedProcessTemplates={adaptedProcessTemplates}
          // machineId={Number(id)}
        />
      </Page>
    </FormikProvider>
  );
};

export { Machines };
